<div class="cw-layout">
  <app-workbench-header></app-workbench-header>
  <main class="cw-layout__main" aria-labelledby="upgrade-title">
    <header class="cw-page-head">
      <div>
        <nav class="cw-breadcrumb" [attr.aria-label]="'job_upgrade_title' | translate: { referenceNumber: job?.referenceNumber }">
          <a routerLink="/openjobs">{{ "open_jobs_summary_title" | translate }}</a>
          @if (siteId && jobType) {
            <span aria-hidden="true">/</span>
            <a [routerLink]="['/openjobs', siteId, jobType]" [queryParams]="{ siteName: siteName }">{{ siteName }} - {{ jobTypeKey | translate }} {{ "open_jobs_list_jobs" | translate }}</a>
          }
          @if (job) {
            <span aria-hidden="true">/</span>
            <a [routerLink]="['/openjobs', siteId, jobType, jobId]" [queryParams]="{ siteName: siteName }">{{ "open_job_detail_job_title" | translate: { referenceNumber: job.referenceNumber } }}</a>
          }
          <span aria-hidden="true">/</span>
          <span aria-current="page">{{ "open_job_detail_lodge_upgrade" | translate }}</span>
        </nav>
        <p class="cw-job-head__site">{{ siteName }}</p>
        <h1 class="cw-page-head__title" id="upgrade-title">
          @if (job) {
            {{ "job_upgrade_title" | translate: { referenceNumber: job.referenceNumber } }}
          } @else {
            {{ "open_job_detail_lodge_upgrade" | translate }}
          }
        </h1>
      </div>
      <div class="cw-thinksafe">
        <img class="cw-thinksafe__logo" src="/assets/thinksafe-logo.png" alt="ThinkSafe" />
        <span class="cw-thinksafe__link">Browse Health &amp; Safety Documents</span>
      </div>
    </header>

  @if (isLoading) {
    <div class="upgrade-state cw-card" role="status" [attr.aria-label]="'signin_lbl_loading_jobs' | translate"><mat-spinner diameter="36"></mat-spinner></div>
  } @else if (loadErrorKey) {
    <div class="upgrade-state upgrade-state--error cw-card" role="alert">
      <mat-icon>error_outline</mat-icon><span>{{ loadErrorKey | translate }}</span>
    </div>
  } @else if (job) {
    <div class="upgrade-intro" role="note">
      <mat-icon aria-hidden="true">info_outline</mat-icon>
      <span [innerHTML]='(upgradeThreshold > 0 ? "job_upgrade_intro_threshold" : "job_upgrade_intro") | translate: { currency: currency, threshold: upgradeThreshold }'></span>
    </div>

    <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
      <section class="upgrade-details cw-card" aria-labelledby="upgrade-details-title">
        <div class="section-heading">
          <h2 id="upgrade-details-title">{{ "job_upgrade_details" | translate }}</h2>
          <span>{{ "job_upgrade_required_hint" | translate }}</span>
        </div>

        <dl class="job-facts">
          <div><dt>{{ "job_upgrade_job" | translate }}</dt><dd>{{ job.referenceNumber }} ({{ job.jobStatusName }})</dd></div>
          <div><dt>{{ "job_upgrade_site" | translate }}</dt><dd>{{ siteName }}</dd></div>
          <div><dt>{{ "job_upgrade_contractor" | translate }}</dt><dd>{{ contractorName || ("open_jobs_summary_not_available" | translate) }}</dd></div>
        </dl>

        <div class="details-grid">
          <mat-form-field appearance="outline">
            <mat-label>{{ "job_upgrade_contact_name" | translate }}</mat-label>
            <input matInput formControlName="contactName" autocomplete="name">
            <mat-error>{{ "job_upgrade_contact_name_required" | translate }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ "job_upgrade_contact_number" | translate }}</mat-label>
            <input matInput type="tel" formControlName="contactNumber" inputmode="numeric" autocomplete="tel">
            <mat-error>{{ (form.controls.contactNumber.hasError('required') ? "job_upgrade_contact_number_required" : "job_upgrade_contact_number_invalid") | translate }}</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" class="description-field">
            <mat-label>{{ "job_upgrade_description" | translate }}</mat-label>
            <textarea matInput rows="5" formControlName="descriptionOfWorks"></textarea>
            <mat-error>{{ "job_upgrade_description_required" | translate }}</mat-error>
          </mat-form-field>
        </div>

        <div class="attachments">
          <div class="attachments__header">
            <strong>{{ "job_upgrade_attachments" | translate }}</strong>
            <label class="attachment-button cw-pill-button">
              <mat-icon aria-hidden="true">add</mat-icon>{{ "job_upgrade_add_attachment" | translate }}
              <input type="file" multiple (change)="selectAttachments($event)" [attr.aria-label]="'job_upgrade_add_attachment' | translate">
            </label>
          </div>
          @for (attachment of attachments; track attachment.name) {
            <div class="attachment-file">
              <mat-icon aria-hidden="true">insert_drive_file</mat-icon>
              <span>{{ attachment.name }}</span>
              <button mat-icon-button type="button" (click)="removeAttachment(attachment)" [attr.aria-label]="('job_upgrade_remove_attachment' | translate) + ': ' + attachment.name"><mat-icon>delete</mat-icon></button>
            </div>
          }
        </div>
      </section>

      <section class="expenses cw-card" aria-labelledby="expenses-title">
        <div class="section-heading section-heading--actions">
          <div>
            <h2 id="expenses-title">{{ "job_upgrade_expenses" | translate }}</h2>
            <span>{{ "job_upgrade_expenses_hint" | translate }}</span>
          </div>
          <button class="cw-pill-button" type="button" (click)="openLineItem()">
            <mat-icon>add</mat-icon>{{ "job_upgrade_add_expense" | translate }}
          </button>
        </div>

        @if (lineItems.length === 0) {
          <div class="empty-expenses">
            <mat-icon>receipt_long</mat-icon>
            <span>{{ "job_upgrade_no_expenses" | translate }}</span>
          </div>
        } @else {
          <div class="expense-table">
            <div class="expense-row expense-row--header" aria-hidden="true">
              <span>{{ "job_upgrade_item" | translate }}</span>
              <span>{{ "job_upgrade_description_short" | translate }}</span>
              <span>{{ "job_upgrade_unit_price" | translate }}</span>
              <span>{{ "job_upgrade_quantity" | translate }}</span>
              <span>{{ "job_upgrade_tax" | translate }}</span>
              <span>{{ "job_upgrade_total" | translate }}</span>
              <span>{{ "job_upgrade_actions" | translate }}</span>
            </div>
            @for (lineItem of lineItems; track $index) {
              <article class="expense-row">
                <span [attr.data-label]="'job_upgrade_item' | translate">{{ lineItem.itemType }}</span>
                <span [attr.data-label]="'job_upgrade_description_short' | translate">{{ lineItem.itemDescription }}</span>
                <span [attr.data-label]="'job_upgrade_unit_price' | translate">{{ currency }}{{ lineItem.unitPrice | number: '1.2-2' }}</span>
                <span [attr.data-label]="'job_upgrade_quantity' | translate">{{ lineItem.itemQuantity | number: '1.0-2' }}</span>
                <span [attr.data-label]="'job_upgrade_tax' | translate">{{ currency }}{{ lineItem.lineTax | number: '1.2-2' }}</span>
                <strong [attr.data-label]="'job_upgrade_total' | translate">{{ currency }}{{ lineItem.lineGross | number: '1.2-2' }}</strong>
                <span class="expense-actions">
                  <button mat-icon-button type="button" (click)="openLineItem(lineItem)" [attr.aria-label]="'job_upgrade_edit_expense' | translate"><mat-icon>edit</mat-icon></button>
                  <button mat-icon-button type="button" (click)="deleteLineItem(lineItem)" [attr.aria-label]="'job_upgrade_delete_expense' | translate"><mat-icon>delete</mat-icon></button>
                </span>
              </article>
            }
          </div>

          <dl class="totals">
            <div><dt>{{ "job_upgrade_net" | translate }}</dt><dd>{{ currency }}{{ totalNet | number: '1.2-2' }}</dd></div>
            <div><dt>{{ "job_upgrade_tax" | translate }}</dt><dd>{{ currency }}{{ totalTax | number: '1.2-2' }}</dd></div>
            <div class="totals__gross"><dt>{{ "job_upgrade_gross" | translate }}</dt><dd>{{ currency }}{{ totalGross | number: '1.2-2' }}</dd></div>
          </dl>
        }
      </section>

      @if (!canSubmit) {
        <p class="submission-hint" role="note"><mat-icon aria-hidden="true">info_outline</mat-icon>{{ "job_upgrade_submission_hint" | translate: { currency: currency, threshold: upgradeThreshold } }}</p>
      }
      @if (submitErrorKey) {
        <div class="form-message" role="alert">{{ submitErrorKey | translate }}</div>
      }

      <div class="form-actions">
        <button class="cw-button cw-button--secondary" type="button" (click)="print()"><mat-icon>print</mat-icon>{{ "job_upgrade_print" | translate }}</button>
        <button class="cw-button cw-button--primary form-actions__submit" type="submit" [disabled]="!canSubmit">
          <mat-icon>send</mat-icon>{{ (isSaving ? "job_upgrade_submitting" : "job_upgrade_submit") | translate }}
        </button>
      </div>
    </form>
  }
  </main>
  <app-workbench-footer></app-workbench-footer>
</div>